import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Play, Radio } from "lucide-react";
import { useContent } from "../../context/ContentContext";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import WhatsAppButton from "../../components/WhatsAppButton";
import VideoModal from "../../components/VideoModal";

const ITEMS = [
  {
    title: "Cantemos con nuestros niños",
    tag: "Formato propio",
    image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_team-workspace-60/artifacts/w75duljq_Cantemos%20con%20nuestros%20ni%C3%B1os.png",
    video: "https://vimeo.com/273926006",
  },
  {
    title: "La noche de amor",
    tag: "Formato propio",
    image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_team-workspace-60/artifacts/yexa6arn_La%20noche%20de%20amor.png",
    video: "https://youtu.be/RCw5QmC8aF4",
  },
];

export default function ProduccionArtisticaPage() {
  const { content, loading } = useContent();
  const [current, setCurrent] = useState(null);

  if (loading || !content) {
    return <div className="min-h-screen bg-black flex items-center justify-center text-white/60 font-mono-caps tracking-widest">CARGANDO...</div>;
  }

  return (
    <div className="relative min-h-screen bg-black text-white overflow-x-hidden">
      <Header />

      {/* HERO — texto + video */}
      <section className="relative min-h-[92vh] flex items-center pt-28 pb-16 md:pt-32 overflow-hidden">
        <img src="https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=1800&q=85" alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-[#ff6a2b]/12 to-black" />
        <div className="absolute inset-0 noise-overlay pointer-events-none" />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center">
          <div className="lg:col-span-6 xl:col-span-6 min-w-0">
            <p className="font-mono-caps text-xs md:text-sm tracking-[0.3em] uppercase text-[#ff8a3c]">Kilombo Music</p>
            <h1 className="mt-6 font-display font-black tracking-tight text-[30px] leading-[1.05] sm:text-[36px] md:text-[38px] lg:text-[38px] xl:text-[46px]">
              Transformamos <span className="text-gradient-music">escenarios en experiencias</span>, y canciones en legado.
            </h1>
            <p className="mt-6 max-w-xl text-white/85 text-base md:text-lg leading-snug">
              Desde hace más de 25 años diseñamos y producimos música: experiencias en vivo para audiencias que salen distintas a como entraron, canciones e identidad para artistas que buscan su propio sonido.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-x-3 gap-y-2 font-mono-caps text-[11px] md:text-xs tracking-[0.18em] uppercase text-white/85">
              <span>Producción Discográfica</span>
              <span className="text-[#ff8a3c]">·</span>
              <span>Shows en Vivo</span>
              <span className="text-[#ff8a3c]">·</span>
              <span>Identidad Artística</span>
            </div>
            <div className="mt-8 md:mt-10">
              <Link to="/contact" className="btn-shine inline-flex items-center gap-3 rounded-full bg-[#ff6a2b] hover:bg-[#ff8a3c] text-black pl-6 pr-5 py-3.5 font-mono-caps text-xs tracking-[0.2em] uppercase transition-colors">
                Hablemos
                <span className="w-7 h-7 rounded-full bg-black text-white flex items-center justify-center"><ArrowRight className="w-3.5 h-3.5" /></span>
              </Link>
            </div>
          </div>

          {/* Embedded Vimeo video */}
          <div className="lg:col-span-6 xl:col-span-6 min-w-0">
            <div className="relative w-full aspect-video rounded-2xl overflow-hidden border border-white/15 shadow-2xl bg-black">
              <iframe
                src="https://player.vimeo.com/video/1219587152?autoplay=1&muted=1&loop=1&background=0&byline=0&title=0&portrait=0&controls=1"
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title="Kilombo Producción Artística"
              />
            </div>
            <p className="mt-3 font-mono-caps text-[10px] md:text-xs tracking-[0.3em] uppercase text-white/50 text-center lg:text-left">
              Reel · Producción Artística
            </p>
          </div>
        </div>
      </section>

      {/* GALLERY — 2 grandes categorías */}
      <section className="relative bg-neutral-950 py-20 md:py-28 px-6 border-y border-white/5">
        <div className="max-w-7xl mx-auto space-y-20 md:space-y-28">
          {[
            {
              cat: "Escenarios hechos experiencia",
              sub: "Shows en Vivo",
              items: [
                { brand: "La Noche de Amor", video: "https://www.youtube.com/shorts/1Pvit5fdKV8" },
                { brand: "Cantemos con Nuestros Niños", video: "https://vimeo.com/273926006" },
                { brand: "Cantemos con Nuestros Niños Navidad", video: "https://vimeo.com/362398297" },
                { brand: "Cantemos con Nuestros Niños Navidad II", video: "https://vimeo.com/459086418" },
                { brand: "Christmas Lounge", video: "https://www.youtube.com/watch?v=dcqE81RRWwY" },
                { brand: "Rock'n Christmas", video: "https://www.youtube.com/shorts/_bdaHJfXl2U" },
                { brand: "La Noche de… (Tu Marca)", video: "https://www.youtube.com/watch?v=RCw5QmC8aF4" },
              ],
            },
            {
              cat: "Canciones hechas legado",
              sub: "Producción Discográfica",
              items: [
                { brand: "Andrés Cepeda", video: "https://www.youtube.com/watch?v=xKULAzXX7pI" },
                { brand: "Marbelle", video: "https://www.tiktok.com/@marbellemusik/video/7406828017013591302" },
                { brand: "Checo Acosta", video: "https://www.youtube.com/watch?v=OdnLvzLstxI" },
                { brand: "Fanny Lu, Maluma y Cepeda", video: "https://vimeo.com/157445489" },
                { brand: "Jessi Uribe", video: "https://vimeo.com/564185234" },
                { brand: "Madame Periné", video: "https://vimeo.com/1221284837" },
                { brand: "Maía", video: "https://www.youtube.com/watch?v=KDWHLmPaTWE" },
                { brand: "Q'arto Aparte", video: "https://www.youtube.com/watch?v=JorzxsyqQgs" },
                { brand: "Marcelo Cezán", video: "https://www.youtube.com/watch?v=Fg-bF572XDQ" },
                { brand: "Majida Issa", video: "https://www.youtube.com/watch?v=dY3rfniFJfQ" },
              ],
            },
            {
              cat: "Canciones hechas legado",
              sub: "Identidad Artística",
              note: "Más de 50 proyectos musicales",
              isImages: true,
              items: [
                { brand: "María del Sol Peralta", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_team-workspace-60/artifacts/dtxtckrx_MARIA%20DEL%20SOL%20PERALTA%20.jpeg" },
                { brand: "María Isabel Saavedra", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_team-workspace-60/artifacts/5wi19a46_MARIA%20ISABEL%20SAAVEDRA.jpeg" },
                { brand: "Boybeck", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_team-workspace-60/artifacts/0xrhtmv7_BOYBECK.jpeg" },
                { brand: "Camilo Montenegro", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_team-workspace-60/artifacts/etrrxa5m_CAMILO%20MONTENEGRO.jpeg" },
                { brand: "Pablo Mantilla", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_team-workspace-60/artifacts/vs9kp9if_PABLO%20MANTILLA.jpeg" },
                { brand: "Cluster Creativo · Cámara de Comercio de Bogotá", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_team-workspace-60/artifacts/ac8w1maw_CLUSTER%20CREATIVO%20C%C3%81MARA%20DE%20COMERCIO%20DE%20BOGOT%C3%81%20.webp" },
              ],
            },
          ].map((grp, gi) => {
            const getThumb = (url) => {
              const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{6,})/);
              if (yt) return `https://img.youtube.com/vi/${yt[1]}/hqdefault.jpg`;
              const vim = url.match(/vimeo\.com\/(\d+)/);
              if (vim) return `https://vumbnail.com/${vim[1]}.jpg`;
              return "https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=800&q=80";
            };
            const showCategoryHeader = gi === 0 || grp.cat !== [
              "Escenarios hechos experiencia","Canciones hechas legado","Canciones hechas legado"
            ][gi-1];
            return (
              <div key={gi}>
                {showCategoryHeader && (
                  <p className="font-mono-caps text-xs tracking-[0.3em] uppercase text-[#ff8a3c] mb-3">— {grp.cat} —</p>
                )}
                <div className="flex items-end justify-between gap-3 flex-wrap mb-8 md:mb-10">
                  <h3 className="font-heading text-3xl md:text-4xl lg:text-5xl tracking-[0.05em] uppercase text-white">
                    {grp.sub}
                  </h3>
                  {grp.note && (
                    <span className="font-mono-caps text-[10px] md:text-xs tracking-[0.2em] uppercase text-[#ffb46b]">{grp.note}</span>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {grp.items.map((it, i) => (
                    grp.isImages ? (
                      <div key={i} className="rounded-2xl border border-white/10 bg-black overflow-hidden flex flex-col">
                        <div className="relative aspect-video overflow-hidden">
                          <img src={it.image} alt={it.brand} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                        </div>
                        <div className="p-5 md:p-6">
                          <h4 className="font-display font-bold text-lg md:text-xl text-white leading-tight">{it.brand}</h4>
                        </div>
                      </div>
                    ) : (
                      <a key={i} href={it.video} target="_blank" rel="noopener noreferrer" className="group rounded-2xl border border-white/10 bg-black overflow-hidden hover:border-[#ff8a3c]/50 transition-colors flex flex-col">
                        <div className="relative aspect-video overflow-hidden">
                          <img src={getThumb(it.video)} alt={it.brand} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105" onError={(e)=>{e.currentTarget.style.background='#111';}} />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent" />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="w-14 h-14 rounded-full bg-black/70 border border-[#ff8a3c] flex items-center justify-center backdrop-blur transition-transform group-hover:scale-110">
                              <Play className="w-5 h-5 text-white ml-0.5" />
                            </span>
                          </div>
                        </div>
                        <div className="p-5 md:p-6">
                          <h4 className="font-display font-bold text-lg md:text-xl text-white leading-tight">{it.brand}</h4>
                        </div>
                      </a>
                    )
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-black py-20 md:py-28 px-6">
        <div className="max-w-4xl mx-auto text-center rounded-3xl border border-[#ff6a2b]/30 bg-gradient-to-br from-[#ff6a2b]/12 via-black to-black p-8 md:p-16">
          <h2 className="font-display font-black text-3xl md:text-4xl lg:text-5xl leading-tight text-white">
            De público a <span className="text-gradient-music">fans</span>. Empecemos.
          </h2>
          <p className="mt-6 text-white/70 text-base md:text-lg max-w-xl mx-auto">
            Producimos canciones e identidad para artistas que buscan su propio sonido.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link to="/contact" className="btn-shine inline-flex items-center gap-3 rounded-full bg-white text-black pl-6 pr-5 py-3.5 font-mono-caps text-xs tracking-[0.2em] uppercase hover:bg-neutral-100 transition-colors">
              Hablemos
              <span className="w-7 h-7 rounded-full bg-[#ff6a2b] text-black flex items-center justify-center"><ArrowRight className="w-3.5 h-3.5" /></span>
            </Link>
          </div>
        </div>
      </section>

      <Footer footer={content.footer} />
      <WhatsAppButton />

      <VideoModal open={!!current} onClose={() => setCurrent(null)} url={current?.video} title={current?.title} />
    </div>
  );
}
